import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Loader2, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { makeAnswerCard, logAnswerCard, startCardCheckout } from "../lib/api";
import { SPECIALISTS, specialistId } from "../lib/specialists";

const Card = React.forwardRef(({ card }, ref) => {
  const s = SPECIALISTS[specialistId(card.speaker)];
  return (
    <div ref={ref} data-testid="answer-card" className="bg-white w-[360px] border-2 border-[#14532D] rounded-2xl p-5 text-slate-900">
      <img src="/brand/phdg-logo.png" alt="GreenFlow Plant Health & Diagnostic Group" className="mx-auto w-36 h-auto" />
      <div data-testid="answer-card-email" className="mt-1 text-center text-[13px] font-bold text-[#14532D]">AskDr.Verdant@greenflowlandcare.com</div>
      <div className="mt-3 border-t-2 border-sky-700 pt-3 flex items-center gap-3">
        <img src={s.face} alt="" className="h-11 w-11 rounded-full object-cover" />
        <div><div className="text-sm font-bold text-[#14532D]">{s.name}</div><div className="text-[11px] text-sky-700">{s.title}</div></div>
      </div>
      <h3 className="mt-3 font-bold text-base leading-snug text-[#092C15]">{card.title}</h3>
      {card.key_result && <div className="mt-2 rounded-xl bg-[#F3F8F5] border border-emerald-200 px-3 py-2 text-sm font-bold text-[#14532D]">{card.key_result}</div>}
      <ul className="mt-2 list-disc pl-5 space-y-1 text-[13px] leading-snug">{card.points.map((p, i) => <li key={i}>{p}</li>)}</ul>
      <p className="mt-3 text-[10px] text-slate-500 leading-snug">Preliminary guidance from Ask Dr. Verdant&trade;. Confirm with a professional before treatment.</p>
    </div>
  );
});

const textOf = (c) => `${c.title}\n${c.key_result ? `${c.key_result}\n` : ""}${c.points.map((p) => `• ${p}`).join("\n")}\n— ${SPECIALISTS[specialistId(c.speaker)].name}, Ask Dr. Verdant`;

export const PENDING_CARD = "gf.card.pending";
const inflight = {};
const requestCard = (sessionId, a) => {
  const key = a.request_id;
  if (!inflight[key]) inflight[key] = makeAnswerCard(sessionId, a.speaker, a.text, key).finally(() => setTimeout(() => delete inflight[key], 5000));
  return inflight[key];
};

const Paywall = ({ info, answer, sessionId }) => {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      localStorage.setItem(PENDING_CARD, JSON.stringify({ ...answer, sessionId, back: window.location.pathname }));
      window.location.href = (await startCardCheckout(sessionId)).checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout is unavailable right now."); setBusy(false); }
  };
  return (
    <div data-testid="answer-card-paywall" className="py-6 text-center">
      <p className="text-sm text-slate-700">You've used your <strong>{info.free} free answer cards</strong> this month.</p>
      <p className="text-sm text-slate-700 mt-1">You can still get this card for <strong>${Number(info.price).toFixed(2)}</strong>.</p>
      <Button data-testid="answer-card-pay" disabled={busy} onClick={pay} className="mt-4 h-12 w-full rounded-xl bg-[#14532D] hover:bg-[#0F3F22] font-bold">
        {busy && <Loader2 size={16} className="mr-2 animate-spin" />}GET THIS CARD FOR ${Number(info.price).toFixed(2)}</Button>
    </div>
  );
};

export default function AnswerCardDialog({ answer, sessionId, onClose }) {
  const [card, setCard] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setCard(null);
    setPaywall(null);
    if (!answer) return;
    requestCard(sessionId, answer).then(setCard).catch((e) => {
      const d = e?.response?.data?.detail;
      if (d?.code === "card_payment_required") return setPaywall(d);
      toast.error(typeof d === "string" ? d : "Couldn't prepare the card.");
      onClose();
    });
  }, [answer]); // eslint-disable-line react-hooks/exhaustive-deps

  const png = async () => {
    const url = await toPng(ref.current, { pixelRatio: 3, backgroundColor: "#ffffff" });
    return { url, file: new File([await (await fetch(url)).blob()], "ask-dr-verdant-answer.png", { type: "image/png" }) };
  };
  const save = async () => {
    setBusy(true);
    try {
      const { url } = await png();
      const a = document.createElement("a");
      a.href = url; a.download = "ask-dr-verdant-answer.png"; a.click();
      logAnswerCard(card.id, "save");
    } catch { toast.error("Couldn't save the image."); }
    setBusy(false);
  };
  const share = async () => {
    setBusy(true);
    try {
      const { file } = await png();
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: card.title }); logAnswerCard(card.id, "share"); }
      else { await navigator.clipboard.writeText(textOf(card)); logAnswerCard(card.id, "copy"); toast.success("Sharing isn't supported here, so the text was copied instead."); }
    } catch { /* user closed the share sheet */ }
    setBusy(false);
  };
  const copy = () => navigator.clipboard.writeText(textOf(card)).then(() => { logAnswerCard(card.id, "copy"); toast.success("Copied"); });

  return (
    <Dialog open={!!answer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="answer-card-dialog" className="z-[80] max-w-[420px] max-h-[92dvh] overflow-y-auto p-4">
        <DialogTitle className="text-base">Save or share this answer</DialogTitle>
        <p className="text-xs text-slate-600 -mt-1">1. Check the card below. 2. Tap <strong>Save picture</strong> to keep it, or <strong>Share / Text</strong> to send it.</p>
        {paywall ? <Paywall info={paywall} answer={answer} sessionId={sessionId} /> : !card ? <div data-testid="answer-card-loading" className="py-16 text-center text-sm text-slate-600"><Loader2 className="mx-auto mb-2 animate-spin" />Tidying up the answer…</div> : (
          <>
            <div className="flex justify-center overflow-x-auto"><Card ref={ref} card={card} /></div>
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="answer-card-save" disabled={busy} onClick={save} className="h-12 rounded-xl bg-[#14532D] hover:bg-[#0F3F22] font-bold"><Download size={16} className="mr-1.5" />Save picture</Button>
              <Button data-testid="answer-card-share" disabled={busy} onClick={share} className="h-12 rounded-xl bg-sky-700 hover:bg-sky-800 font-bold"><Share2 size={16} className="mr-1.5" />Share / Text</Button>
            </div>
            <button data-testid="answer-card-copy" onClick={copy} className="mx-auto flex items-center gap-1 text-xs text-slate-600 underline"><Copy size={12} />Copy as text instead</button>
            {card.free_left != null && (
              <p data-testid="answer-card-allowance" className="text-center text-xs font-semibold text-[#14532D] bg-[#F3F8F5] rounded-lg py-2 px-3">
                {card.free_left > 0
                  ? `${card.free_left} free card${card.free_left === 1 ? "" : "s"} left this month. After that, cards are $${Number(card.card_price).toFixed(2)} each.`
                  : `No free cards left this month. More cards are $${Number(card.card_price).toFixed(2)} each.`}
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
